// src/components/dashboard/client/ClientDiscussion.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  onSnapshot,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, auth, storage } from '../../../firebase/config';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  User,
  Clock,
  Eye
} from 'lucide-react';

const ClientDiscussion = () => {
  const { dossierId, discussionId } = useParams();
  const navigate = useNavigate();
  const [discussion, setDiscussion] = useState(null);
  const [dossierData, setDossierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentUser = auth.currentUser;

  // Couleurs pastels pour les différents rôles
  const getUserColor = (userId, userName, isSystem) => {
    if (isSystem) return 'bg-gray-100 text-gray-700 border border-gray-200';
    if (userId === currentUser?.uid) return 'bg-blue-100 text-blue-800 border border-blue-200';
    
    // Couleurs pastels basées sur le rôle ou nom d'utilisateur
    if (userName?.toLowerCase().includes('admin') || userName?.toLowerCase().includes('super')) {
      return 'bg-red-100 text-red-700 border border-red-200';
    }
    if (userName?.toLowerCase().includes('charge')) {
      return 'bg-green-100 text-green-700 border border-green-200';
    }
    if (userName?.toLowerCase().includes('chef')) {
      return 'bg-purple-100 text-purple-700 border border-purple-200';
    }
    
    // Couleur par défaut pour les autres
    return 'bg-orange-100 text-orange-700 border border-orange-200';
  };

  const getUserInitial = (userName, isSystem) => {
    if (isSystem) return 'S';
    if (!userName) return 'U';
    
    if (userName.toLowerCase().includes('admin') || userName.toLowerCase().includes('super')) {
      return 'S';
    }
    if (userName.toLowerCase().includes('client')) {
      return 'C';
    }
    
    return userName.charAt(0).toUpperCase();
  };

  const getUserRole = (userName, isSystem, userId) => {
    if (isSystem) return 'Super Admin';
    if (userId === currentUser?.uid) return 'Client';
    if (userName?.toLowerCase().includes('admin') || userName?.toLowerCase().includes('super')) {
      return 'Super Admin';
    }
    if (userName?.toLowerCase().includes('charge')) {
      return 'Chargé d\'affaires';
    }
    if (userName?.toLowerCase().includes('chef')) {
      return 'Chef de projet';
    }
    return 'Équipe';
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Obtenir l'icône du fichier selon son type
  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
    return '📎';
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Charger les données et écouter en temps réel
  useEffect(() => {
    const loadData = async () => {
      if (!dossierId || !discussionId) return;
      
      try {
        // Charger le dossier
        const dossierDoc = await getDoc(doc(db, "dossiers", dossierId));
        if (dossierDoc.exists()) {
          setDossierData(dossierDoc.data());
        }

        // Écouter la discussion en temps réel
        const unsubscribe = onSnapshot(
          doc(db, "discussions", discussionId),
          (discussionDoc) => {
            if (discussionDoc.exists()) {
              setDiscussion({ id: discussionDoc.id, ...discussionDoc.data() });
            }
            setLoading(false);
          },
          (error) => {
            console.error("Erreur lors du chargement de la discussion:", error);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setLoading(false);
      }
    };

    const unsubscribe = loadData();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [dossierId, discussionId]);

  // Faire défiler vers le bas
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [discussion?.messages]);

  // Upload de fichier
  const handleFileUpload = async (file) => {
    if (!file || !currentUser || uploading) return;

    console.log("🔄 Début de l'upload:", file.name, file.size);
    console.log("👤 Utilisateur connecté:", currentUser.uid);
    console.log("💬 Discussion ID:", discussionId);

    // Vérifier la taille du fichier (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximum : 10MB');
      return;
    }

    setUploading(true);
    try {
      // Vérifier que Firebase Storage est disponible
      if (!storage) {
        throw new Error('Firebase Storage non configuré');
      }

      console.log("📂 Storage disponible:", !!storage);
      console.log("📂 Storage bucket:", storage.app.options.storageBucket);
      
      // Créer une référence unique pour le fichier
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `discussions/${discussionId}/${fileName}`;
      console.log("📁 Chemin du fichier:", filePath);
      
      const fileRef = ref(storage, filePath);
      console.log("📂 Référence créée:", fileRef.fullPath);
      
      console.log("⬆️ Début upload...");
      // Upload du fichier
      const snapshot = await uploadBytes(fileRef, file);
      console.log("✅ Upload terminé:", snapshot.metadata.fullPath);
      console.log("📊 Taille uploadée:", snapshot.metadata.size);
      
      console.log("🔗 Récupération de l'URL...");
      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log("✅ URL récupérée:", downloadURL);
      
      // Créer le message avec la pièce jointe
      const message = {
        text: `Fichier partagé : ${file.name}`,
        createdAt: Timestamp.now(),
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || 'Client',
        system: false,
        readBy: [currentUser.uid],
        attachment: {
          name: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type
        }
      };

      console.log("💬 Ajout du message...");
      await updateDoc(doc(db, "discussions", discussionId), {
        messages: arrayUnion(message),
        updatedAt: Timestamp.now(),
        lastMessage: `${getUserRole(message.userName, false, currentUser.uid)}: Fichier partagé`,
        readBy: [currentUser.uid] // ✅ CORRECTION : Reset readBy discussion
      });

      console.log("✅ Message ajouté avec succès!");
      alert("Fichier uploadé avec succès !");

    } catch (error) {
      console.error("❌ Erreur lors de l'upload:", error);
      console.error("❌ Détails de l'erreur:", error.message);
      console.error("❌ Code d'erreur:", error.code);
      console.error("❌ Stack trace:", error.stack);
      alert(`Erreur lors de l'upload du fichier: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Envoyer un message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || sending) return;

    setSending(true);
    try {
      const message = {
        text: newMessage.trim(),
        createdAt: Timestamp.now(),
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || 'Client',
        system: false,
        readBy: [currentUser.uid],
        attachment: null
      };

      await updateDoc(doc(db, "discussions", discussionId), {
        messages: arrayUnion(message),
        updatedAt: Timestamp.now(),
        lastMessage: `${getUserRole(message.userName, false, currentUser.uid)}: ${message.text.substring(0, 50)}${message.text.length > 50 ? '...' : ''}`,
        readBy: [currentUser.uid] // ✅ CORRECTION : Reset readBy discussion
      });

      setNewMessage('');
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
      alert("Erreur lors de l'envoi du message. Veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  // Marquer un message comme lu
  const markMessageAsRead = async (messageIndex) => {
    if (!discussion?.messages?.[messageIndex] || !currentUser) return;
    
    const message = discussion.messages[messageIndex];
    if (!message.readBy || !message.readBy.includes(currentUser.uid)) {
      const updatedMessages = [...discussion.messages];
      if (!updatedMessages[messageIndex].readBy) {
        updatedMessages[messageIndex].readBy = [];
      }
      updatedMessages[messageIndex].readBy.push(currentUser.uid);
      
      try {
        await updateDoc(doc(db, "discussions", discussionId), {
          messages: updatedMessages
        });
      } catch (error) {
        console.error("Erreur lors du marquage comme lu:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!discussion || !dossierData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Discussion non trouvée</h2>
          <button
            onClick={() => navigate(`/clients/${dossierId}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/clients/${dossierId}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {dossierData.titre}
              </h1>
              <p className="text-sm text-gray-600">Discussion avec l'équipe</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Zone de messages */}
          <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
            {discussion.messages?.map((message, index) => {
              const isSystem = message.system;
              const userRole = getUserRole(message.userName, isSystem, message.userId);
              const userInitial = getUserInitial(message.userName, isSystem);
              const colorClasses = getUserColor(message.userId, message.userName, isSystem);
              const readByCount = message.readBy ? message.readBy.length : 0;
              
              return (
                <div key={index} className="space-y-2">
                  {/* En-tête du message */}
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${colorClasses}`}>
                      {userInitial}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {isSystem ? (message.text.startsWith('Dossier créé') ? message.text : userRole) : userRole}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(message.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Contenu du message */}
                  {!isSystem && (
                    <div className="ml-11">
                      <div className={`rounded-lg p-4 text-gray-900 shadow-sm ${
                        message.userId === currentUser?.uid 
                          ? 'bg-blue-50 border border-blue-200' 
                          : isSystem 
                            ? 'bg-gray-50 border border-gray-200'
                            : message.userName?.toLowerCase().includes('admin') || message.userName?.toLowerCase().includes('super')
                              ? 'bg-red-50 border border-red-200'
                              : message.userName?.toLowerCase().includes('charge')
                                ? 'bg-green-50 border border-green-200'
                                : message.userName?.toLowerCase().includes('chef')
                                  ? 'bg-purple-50 border border-purple-200'
                                  : 'bg-orange-50 border border-orange-200'
                      }`}>
                        <div className="whitespace-pre-wrap mb-2">
                          {message.text}
                        </div>
                        
                        {/* Affichage de la pièce jointe */}
                        {message.attachment && (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 flex items-center space-x-3">
                            <div className="text-2xl">
                              {getFileIcon(message.attachment.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {message.attachment.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatFileSize(message.attachment.size)}
                              </div>
                            </div>
                            <a
                              href={message.attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                            >
                              Télécharger
                            </a>
                          </div>
                        )}
                      </div>
                      
                      {/* Indicateur de lecture */}
                      <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                        <Eye className="w-3 h-3" />
                        <span>Lu par {readByCount}</span>
                        {!message.readBy?.includes(currentUser?.uid) && (
                          <button
                            onClick={() => markMessageAsRead(index)}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            Marquer comme lu
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Message système spécial pour création de dossier */}
                  {isSystem && message.text.startsWith('Dossier créé') && (
                    <div className="ml-11 bg-blue-50 rounded-lg p-4">
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Détails du dossier</h4>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p><strong>Type:</strong> {dossierData.typeClient || dossierData.type}</p>
                            <p><strong>Adresse:</strong> {dossierData.adresseClient || dossierData.adresse}</p>
                            <p><strong>Source:</strong> {dossierData.sourceContact}</p>
                            <p><strong>Raccordement:</strong> {dossierData.raccordement}</p>
                            {dossierData.description && (
                              <p><strong>Description:</strong> {dossierData.description}</p>
                            )}
                          </div>
                        </div>
                        
                        {discussion.participantNames && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Participants</h4>
                            <p className="text-sm text-gray-700">
                              {Object.values(discussion.participantNames).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Formulaire d'envoi */}
          <div className="border-t p-4">
            <form onSubmit={handleSendMessage} className="space-y-3">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Joindre un fichier"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600"></div>
                  ) : (
                    <Paperclip className="w-5 h-5" />
                  )}
                </button>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Tapez votre message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows="3"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>{sending ? 'Envoi...' : 'Envoyer'}</span>
                </button>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.zip,.rar,.txt"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    console.log("📁 Fichier sélectionné:", file.name, file.size);
                    console.log("🔧 Firebase Storage disponible:", !!storage);
                    console.log("👤 Utilisateur connecté:", !!currentUser);
                    console.log("💬 Discussion ID:", discussionId);
                    handleFileUpload(file);
                  }
                }}
              />
              
              <p className="text-xs text-gray-500">
                Appuyez sur Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne
                {uploading && <span className="text-blue-600"> • Upload en cours...</span>}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDiscussion;